import { useCallback, useEffect, useState } from "react";
import { Plus, Trash2, Save, Lock } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { PageHeader, Pill } from "@/components/common";
import { SettingsTabs } from "@/components/settings/SettingsTabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";

const PermList = ({ perms, value, onChange, disabled, idPrefix }) => (
  <div className="grid sm:grid-cols-2 gap-2">
    {perms.map((p) => (
      <label key={p.key} className="flex items-start gap-2 text-sm text-slate-700 rounded-lg px-2 py-1.5 hover:bg-slate-50 cursor-pointer">
        <Checkbox
          checked={disabled || value.includes(p.key)} disabled={disabled}
          onCheckedChange={(on) => onChange(on ? [...value, p.key] : value.filter((k) => k !== p.key))}
          data-testid={`${idPrefix}-perm-${p.key}`} className="mt-0.5"
        />
        {p.label}
      </label>
    ))}
  </div>
);

const RoleCard = ({ role, perms, roles, onSaved }) => {
  const isNew = !role.id;
  const [name, setName] = useState(role.name);
  const [value, setValue] = useState(role.permissions);
  const [manages, setManages] = useState(role.manages || []);
  const [busy, setBusy] = useState(false);
  const tid = isNew ? "new-role" : `role-${role.key}`;

  const save = async () => {
    setBusy(true);
    try {
      if (isNew) await api.post("/roles", { name, permissions: value, manages });
      else await api.put(`/roles/${role.id}`, { name, permissions: value, manages });
      toast.success(isNew ? "Role created" : "Role saved");
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const remove = async () => {
    if (!window.confirm(`Delete the role "${role.name}"?`)) return;
    try {
      await api.delete(`/roles/${role.id}`);
      toast.success("Role deleted");
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div className={`rounded-2xl border bg-white p-4 sm:p-5 space-y-3 fade-up ${isNew ? "border-[#24474A]/40 border-dashed" : "border-slate-200"}`} data-testid={`${tid}-card`}>
      <div className="flex flex-wrap items-center gap-2">
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Role name" className="max-w-xs font-bold" data-testid={`${tid}-name-input`} />
        {!isNew && <Pill cls="bg-slate-50 text-slate-600 border-slate-200">{role.user_count} user(s)</Pill>}
        <div className="ml-auto flex gap-2">
          {!isNew && <Button variant="ghost" size="icon" onClick={remove} data-testid={`${tid}-delete-btn`}><Trash2 className="w-4 h-4 text-rose-600" /></Button>}
          <Button className="rounded-full" onClick={save} disabled={busy || !name.trim()} data-testid={`${tid}-save-btn`}><Save className="w-4 h-4 mr-1.5" /> {isNew ? "Create role" : "Save"}</Button>
        </div>
      </div>
      <PermList perms={perms} value={value} onChange={setValue} idPrefix={tid} />
      <div className="pt-2 border-t border-slate-100">
        <div className="text-[11px] uppercase tracking-[0.15em] text-slate-500 px-2 pt-2 pb-1">Can manage users with these roles</div>
        <p className="text-xs text-slate-500 px-2 pb-2">Lets this role see their schedules, assign them tasks, edit and approve their schedules and leave (based on the permissions ticked above).</p>
        <PermList perms={roles.map((r) => ({ key: r.key, label: r.name }))} value={manages} onChange={setManages} idPrefix={`${tid}-manages`} />
      </div>
    </div>
  );
};

export default function RolesSettings() {
  const [roles, setRoles] = useState([]);
  const [perms, setPerms] = useState([]);
  const [adding, setAdding] = useState(false);
  const [version, setVersion] = useState(0);

  const load = useCallback(async () => {
    const [r, p] = await Promise.all([api.get("/roles"), api.get("/permissions")]);
    setRoles(r.data);
    setPerms(p.data);
    setAdding(false);
    setVersion((v) => v + 1);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div>
      <PageHeader title="Settings" subtitle="Create roles and choose exactly what each one can do.">
        <Button className="rounded-full" onClick={() => setAdding(true)} disabled={adding} data-testid="new-role-btn"><Plus className="w-4 h-4 mr-1.5" /> New role</Button>
      </PageHeader>
      <SettingsTabs />
      <div className="space-y-4">
        {adding && <RoleCard role={{ name: "", permissions: [], manages: [] }} perms={perms} roles={roles} onSaved={load} />}
        <div className="rounded-2xl border border-slate-800 bg-slate-800 text-white p-4 sm:p-5" data-testid="role-super-admin-card">
          <div className="flex items-center gap-2 font-bold text-lg"><Lock className="w-4 h-4" /> Super Admin</div>
          <p className="text-sm text-white/70 mt-1">Built-in developer account with every permission and access to users of all roles. It cannot be renamed or deleted.</p>
        </div>
        {roles.map((r) => <RoleCard key={`${r.id}-${version}`} role={r} perms={perms} roles={roles} onSaved={load} />)}
      </div>
    </div>
  );
}
